import { CallsService, type CallsListData, type CallsListResponse } from '@/types/generated'

export interface CallTabCounts {
  newCallsCount: number
  closedCallsCount: number
}

export type CallsListFn = (data: CallsListData) => Promise<CallsListResponse>

const defaultCallsList: CallsListFn = (data) => CallsService.callsList(data)

/**
 * Fetch the counts that drive the call list "New" / "Closed" tab badges.
 *
 * The case lifecycle (a call flips to ``HANDLED`` when ingress auto-creates a
 * case) is owned by lawyer/clerk roles. Phone-AI-only users do not own that
 * lifecycle, so the tabs are hidden for them and we only need a single
 * unfiltered total — the closed bucket is forced to ``0`` to match the hidden
 * UI. All other roles still get the open|in_progress vs handled split via two
 * parallel filtered requests.
 *
 * The ``callsList`` parameter is injectable so the decision logic (which
 * request shape to issue, how to map counts) can be unit-tested without
 * standing up the generated HTTP client.
 */
export const fetchCallTabCounts = async (
  options: { isPhoneAiUser: boolean },
  callsList: CallsListFn = defaultCallsList
): Promise<CallTabCounts> => {
  if (options.isPhoneAiUser) {
    const total = await callsList({ hasResult: true, limit: 1 })
    return { newCallsCount: total.count ?? 0, closedCallsCount: 0 }
  }

  const [newResp, closedResp] = await Promise.all([
    callsList({ hasResult: true, limit: 1, status: 'open,in_progress' }),
    callsList({ hasResult: true, limit: 1, status: 'handled' })
  ])

  return {
    newCallsCount: newResp.count ?? 0,
    closedCallsCount: closedResp.count ?? 0
  }
}
